import cx from 'classnames';
import {useCallback} from 'react';
import type {
  CategoryPositionType,
  InvitationCategoryType,
} from '~/common/components/MegaNav/MegaNavVariant/types';
import {titleToId} from '~/common/components/MegaNav/MegaNavVariant/types';
import {Link} from '~library/atoms/Link';
import {useTracking} from '~library/context/tracking/tracking-context';
import {charcoal} from '~libSass/base/_exports.sass';
import * as styles from './Categories.module.scss';

type CategoriesProps = {
  categories: InvitationCategoryType[];
  hoveredCategory: InvitationCategoryType | null;
  handleCategoryHover: (category: InvitationCategoryType, position: CategoryPositionType) => void;
};

export const Categories = ({categories, hoveredCategory, handleCategoryHover}: CategoriesProps) => {
  const {track} = useTracking();

  const renderCategoryLink = useCallback(
    (category: InvitationCategoryType) => {
      const categoryId = titleToId(category.title);
      const hoveredCategoryId = hoveredCategory ? titleToId(hoveredCategory.title) : null;
      const isActive = hoveredCategoryId === categoryId;

      const handleMouseEnter = (event: React.MouseEvent<HTMLButtonElement>): void => {
        const button = event.currentTarget;
        const rect = button.getBoundingClientRect();

        if (rect) {
          const position: CategoryPositionType = {
            left: rect.left,
          };
          handleCategoryHover(category, position);
        }
      };

      const handleClick = (): void => {
        track({
          dataQaId: `category-${categoryId}`,
          href: category.href,
          title: category.title,
          categoryId: categoryId,
        });
      };

      return (
        <Link
          key={categoryId}
          href={category.href}
          data-qa-id={`category-${categoryId}`}
          className={cx(styles.categoryLink, {[styles.active]: isActive})}
          onMouseOver={handleMouseEnter}
          onClick={handleClick}
          style={{
            color: category.linkColor || charcoal,
          }}
        >
          {category.title}
        </Link>
      );
    },
    [hoveredCategory, handleCategoryHover, track]
  );

  return (
    <div className={styles.categoriesContainer} id="categories">
      {categories?.map(renderCategoryLink)}
    </div>
  );
};
